import { requestCodexText } from './codexText';
/**
 * Codex AI Character Analysis Service
 *
 * Provides AI-powered character concept analysis and prompt splitting:
 * - analyzeCharacterConcept: Analyzes user description, returns tags and cultural context
 * - splitCharacterPrompt: Splits description into body and head prompts
 *
 * Used by the new Character Builder prompt flow.
 */

import { CharacterForm, CharacterArchetype, ReferenceImage } from '../types';
import { filterNsfwTriggeringWords, filterNsfwTriggeringTags } from './geminiPromptEnhancer';

// ============================================
// TYPES
// ============================================

// Medium/archetype descriptions for AI context
const MEDIUM_CONTEXT: Record<CharacterArchetype, { name: string; description: string; culturalSources: string }> = {
  film: {
    name: 'Photorealistic/Film',
    description: 'Cinema-quality hyper-realistic character',
    culturalSources: 'Hollywood films, Netflix series, fashion photography, celebrity appearances, runway models, photorealistic CGI in movies like Avatar or The Mandalorian'
  },
  game_3d: {
    name: '3D Video Game',
    description: 'Game-ready 3D character with stylized realism',
    culturalSources: 'AAA video games like Final Fantasy, Mass Effect, God of War, Horizon Zero Dawn, The Last of Us, League of Legends, Overwatch character designs'
  },
  manga_anime: {
    name: 'Manga/Anime',
    description: 'Japanese animation style illustration',
    culturalSources: 'Studio Ghibli, MAPPA, ufotable, Wit Studio, anime series, manga illustrations, light novel art, visual novels, gacha games like Genshin Impact, vtuber designs'
  },
  comic_cartoon: {
    name: 'Comic/Cartoon',
    description: 'Western comics and cartoon style',
    culturalSources: 'Marvel Comics, DC Comics, Image Comics, Disney, Pixar, western graphic novels, superhero films, animated series, Invincible, Spider-Verse style'
  }
};

// Censorship types for body generation
// Determines what underwear/coverage is needed during SFW body generation
export type CensorshipType =
  | 'female_censored'      // Humanoid female - top and bottom underwear required
  | 'male_censored'        // Humanoid male - bottom underwear required
  | 'creature_uncensored'  // Creature without visible sex organs (dragon, slime, robot)
  | 'creature_female_censored'  // Creature with female sex organs - needs coverage
  | 'creature_male_censored';   // Creature with male sex organs - needs coverage

export interface CharacterConceptAnalysis {
  suggestedTags: string[];
  culturalContext: string[];  // Multiple insights for auto-rotation
  inferredGender: 'male' | 'female' | 'neutral';
  characterType: string;  // e.g., "undead", "monster", "fantasy humanoid"
  // Censorship classification - determines underwear requirements for body generation
  censorshipType: CensorshipType;
}

export interface SplitPrompts {
  bodyPrompt: string;
  headPrompt: string;
  sharedAttributes: {
    gender: 'male' | 'female' | 'neutral';
    ethnicity: string;
    skinTone: string;
    age?: string;
  };
  // Classified reference images - each image gets assigned to head, body, or both
  classifiedImages?: {
    imageId: string;
    classification: 'head' | 'body' | 'both';
  }[];
}

// ============================================
// SYSTEM PROMPTS
// ============================================

const CONCEPT_ANALYSIS_SYSTEM_PROMPT = `You are a character design expert analyzing user descriptions for a character creation tool.

Your job is to:
1. Analyze the user's character description IN THE CONTEXT OF THE SPECIFIED MEDIUM
2. Suggest relevant visual tags/attributes the user might want to add (appropriate for the medium)
3. Provide cultural context about how this character type is typically depicted IN THAT SPECIFIC MEDIUM
4. Infer the character's gender if not explicitly stated
5. Determine the censorship type for SFW body generation

CENSORSHIP TYPE CLASSIFICATION:
The character will be generated in underwear for SFW purposes. Classify based on BODY SHAPE:

DECISION RULE: Does it have a HUMANOID BODY (bipedal with humanoid torso, hips, and groin area)?
- YES humanoid body → "male_censored" or "female_censored"
- NO humanoid body → creature types

- "female_censored": Humanoid body shape + female
- "male_censored": Humanoid body shape + male
- "creature_uncensored": Non-humanoid body, no mammalian sex organs
- "creature_female_censored": Non-humanoid lower body + visible female anatomy
- "creature_male_censored": Non-humanoid lower body + visible male anatomy

RESPONSE FORMAT (JSON):
{
  "suggestedTags": ["tag1", "tag2", "tag3", ...],  // 6-10 visual attributes user might add
  "culturalContext": [
    "Context insight 1 about how this character type appears in THIS MEDIUM...",
    "Context insight 2 about common depictions in THIS MEDIUM...",
    "Context insight 3 about visual tropes in THIS MEDIUM..."
  ],
  "inferredGender": "male" | "female" | "neutral",
  "characterType": "category like undead, fantasy, sci-fi, etc.",
  "censorshipType": "female_censored" | "male_censored" | "creature_uncensored" | "creature_female_censored" | "creature_male_censored"
}

MEDIUM-SPECIFIC GUIDELINES:
- Your suggestions and context MUST be tailored to the specified medium
- For anime/manga: reference anime series, manga, light novels, visual novels, gacha games
- For film/photorealistic: reference movies, TV shows, fashion, photography
- For 3D games: reference AAA video games, game franchises, esports characters
- For comics/cartoons: reference Marvel, DC, indie comics, western animation

CULTURAL CONTEXT GUIDELINES:
- Draw from sources APPROPRIATE TO THE MEDIUM specified
- Be specific about visual traits typical in that medium
- Reference notable characters/works from that medium
- Provide 3-5 distinct insights that rotate/cycle for the user

FICTIONAL CHARACTER RECOGNITION:
When user names a KNOWN CHARACTER (anime, movie, game, comic, etc.), suggest their SIGNATURE PHYSICAL TRAITS including ethnicity/heritage:
- "Naruto Uzumaki" → "Japanese features", "spiky blonde hair", "bright blue eyes", "three whisker marks on each cheek", "tanned skin", "athletic teenage build"
- "Geralt of Rivia" → "Eastern European features", "white/silver hair", "cat-like yellow eyes", "facial scars", "muscular weathered build", "pale skin"
- "Wonder Woman" → "Greek/Mediterranean features", "long black hair", "blue eyes", "athletic Amazonian build", "olive skin", "strong jawline"
- "Goku" → "Japanese features", "spiky black hair", "black eyes", "muscular build", "tanned skin", "youthful face"
- "2B (Nier)" → "East Asian features", "short white hair", "blindfolded", "pale skin", "slender athletic build", "beauty mark below lip"
- "Black Panther/T'Challa" → "African features", "dark brown skin", "short black hair", "strong jawline", "regal bearing", "athletic muscular build"
- "Mulan" → "East Asian/Chinese features", "long black hair", "almond-shaped dark eyes", "fair skin", "slender athletic build"

CREATURE/ANIMAL BREEDS & SPECIES:
When user mentions a CREATURE or ANIMAL, suggest BREED/SPECIES-SPECIFIC physical traits:
- "wolf" → suggest breed variations: "grey wolf thick fur", "arctic wolf white coat", "timber wolf large build"
- "dragon" → suggest species traits: "Western dragon leathery wings", "Eastern dragon serpentine body", "wyvern two-legged stance"
- "cat" → suggest breed: "Persian flat face fluffy coat", "Siamese pointed coloring blue eyes", "Maine Coon large tufted ears"
- "dog" → suggest breed: "German Shepherd tan and black coat", "Husky blue eyes thick double coat", "Corgi short legs long body"
- "horse" → suggest breed: "Arabian dished face arched neck", "Clydesdale feathered hooves large build", "Friesian black coat flowing mane"

⚠️ FANTASY HUMANOID RACES (CRITICAL - ALWAYS SUGGEST RACE-DEFINING FEATURES):
When user mentions a FANTASY RACE, ALWAYS suggest their DISTINCTIVE NON-HUMAN features as the FIRST tags:
- "elf" / "elven" → MUST suggest: "long pointed elf ears", "elongated ear points", "ethereal angular features", "luminous eyes", "ageless beauty"
- "dark elf" / "drow" → MUST suggest: "long pointed ears", "grey/ashen skin", "white hair", "red or violet eyes"
- "orc" / "half-orc" → MUST suggest: "prominent lower tusks", "green skin", "heavy brow ridge", "broad flat nose", "muscular hulking build"
- "dwarf" → MUST suggest: "stocky compact build", "broad shoulders", "barrel chest", "thick beard", "weathered ruddy skin"
- "tiefling" → MUST suggest: "curved demonic horns", "red or purple skin", "solid-colored eyes", "pointed tail"
- "dragonborn" → MUST suggest: "scaled reptilian skin", "draconic snout", "no nose", "frills or ridges", "clawed hands"
- "vampire" → MUST suggest: "pale deathly complexion", "sharp fanged canines", "red or pale eyes", "sunken aristocratic features"
- "demon" / "succubus" → MUST suggest: "curved horns", "unnatural skin color", "bat wings", "pointed tail", "clawed hands"

IMPORTANT: Fantasy race features should be the PRIMARY suggested tags, not secondary. The race-defining trait (ears, tusks, horns, skin color) is MORE IMPORTANT than generic features like "attractive" or "fit build".

The goal is to help users recreate recognizable characters with accurate physical details including cultural heritage and species-specific traits.

SUGGESTED TAGS GUIDELINES:
- Focus ONLY on PERMANENT PHYSICAL body attributes and features
- INCLUDE anatomy: breasts, buttocks, body shape, curves, muscles
- INCLUDE: skin details, body type, muscle definition, scars, tattoos, birthmarks, freckles, body hair, physique, bone structure, body proportions, skin texture
- INCLUDE: facial features, eye shape/color, hair texture/color/style, facial hair, ears, teeth, nose shape
- INCLUDE creature anatomy: scales, fur, claws, horns, wings, tails, fangs, extra limbs
- Example: "Aphrodite" → suggest "large breasts", "hourglass figure", "flawless skin", "golden hair"
- Example: "dragon" → suggest "sharp claws", "leathery wings", "scaled hide", "horned head"

NEVER SUGGEST:
- Style keywords (anime, photorealistic, cel shaded, etc.)
- Lighting (dramatic, soft, studio, volumetric, etc.)
- Backgrounds, environments, locations
- Camera angles, zoom, framing, aspect ratios
- Poses, postures, stances, body positions
- Clothing, armor, outfits, accessories, jewelry, weapons
- Color grading, filters, effects, accents
- Technical terms (8K, high quality, resolution, etc.)
- Blood, gore, wounds, injuries, dismemberment (scars are OK - they're healed)

IMPORTANT - FILTERING USER INPUT:
- If the user's description includes clothing, accessories, weapons, backgrounds, lighting, environments, poses, or postures, IGNORE these in your analysis
- Only extract and expand upon PHYSICAL attributes from their description
- Example: "samurai with katana in fighting stance" → only suggest physical traits (muscular build, stern expression, traditional Japanese features), NOT the katana, armor, or fighting stance

EXPAND COUNT HANDLING:
- When expandCount > 0, provide DIFFERENT tags than before
- Use the previousTags list to avoid repetition
- Go deeper into medium-specific details on subsequent expands`;

const PROMPT_SPLIT_SYSTEM_PROMPT = `You are a character prompt specialist. Your job is to split a character description into TWO separate prompts: one for BODY generation and one for HEAD generation.

⚠️ CRITICAL - FULL COHERENCE BETWEEN BODY AND HEAD:
The body and head will be generated SEPARATELY and then composited together.
You MUST ensure BOTH prompts share ALL key visual attributes so they match perfectly when combined.

MANDATORY SHARED ATTRIBUTES - Define these ONCE, use in BOTH prompts:
1. SURFACE: Same skin/fur/scales/hide tone + texture in both
2. HAIR/FUR/MANE COLOR: Same color in both (body: "black fur", head: "black furred face")
3. SPECIES FEATURES: Same creature type traits implied
4. AGE/MATURITY: Same apparent age or life stage

⚠️ FILLING IN GAPS - CRITICAL:
If user doesn't specify an attribute, YOU MUST choose a consistent value for BOTH prompts:
- No hair/fur color specified? Pick one and use it in BOTH
- No skin/scale/fur tone specified? Infer from character type and use in BOTH
- No eye color specified? Pick one that matches the character
- For creatures: ensure species-consistent features (scales, fur pattern, horn style, etc.)
- Stay true to user's intent - don't add random features they didn't imply

BODY PROMPT MUST INCLUDE:
1. Explicit surface (skin/fur/scales) tone AND texture
2. Body type, physique, build, muscle definition
3. Body proportions (height, limb length, body shape)
4. Surface details (scars, markings, patterns, tattoos) - only if user mentioned them
5. Basic face placeholder (details come from head layer)
6. Hair/fur/mane COLOR that matches the head

HEAD PROMPT MUST INCLUDE:
1. SAME surface tone AND texture as body (copy exactly)
2. Face/head structure (face shape, snout, muzzle, jawline)
3. Eye details (color, shape, pupil type)
4. Nose/snout and mouth details
5. Hair/fur/mane with SAME COLOR as body, plus style details
6. EXCLUDE anything below the neck - no shoulders, no chest, no clothing

FILTERING RULES - VERY IMPORTANT:
- NEVER include clothing, armor, outfits, costumes, uniforms, or any garments
- NEVER include accessories, jewelry, weapons, props, or held items
- NEVER include poses, stances, postures, or body positions
- NEVER include backgrounds, environments, scenery, or settings
- NEVER include lighting setups, atmospheres, or weather
- NEVER include aspect ratios, framing, camera angles, or composition terms
- If the user mentions any of these, IGNORE them completely

SHARED ATTRIBUTES:
- Extract gender, ethnicity, skin tone, approximate age
- These will be used to maintain consistency between layers

RESPONSE FORMAT (JSON):
{
  "bodyPrompt": "Enhanced body description - MUST start with skin tone/texture...",
  "headPrompt": "Enhanced head description - MUST start with SAME skin tone/texture as body...",
  "sharedAttributes": {
    "gender": "male" | "female" | "neutral",
    "ethnicity": "Caucasian" | "East Asian" | "African" | "South Asian" | "Latin American" | "Middle Eastern" | "Mixed" | "Fantasy",
    "skinTone": "pale" | "fair" | "medium" | "olive" | "tan" | "brown" | "dark brown" | "ebony" | "fantasy-colored",
    "age": "child" | "teen" | "young adult" | "adult" | "middle-aged" | "elderly" | "ageless"
  }
}

⚠️ FANTASY HUMANOID RACES - HEAVY EMPHASIS REQUIRED:
When the character is a fantasy race (elf, orc, tiefling, etc.), you MUST:
1. Use weight syntax for race-defining features: (pointed elf ears:1.5)
2. REPEAT the distinctive feature 2-3 times with variations
3. Put the race feature EARLY in the prompt, not buried at the end

EXAMPLE 1 - ELF (race features heavily emphasized):
User: "female elf"
{
  "bodyPrompt": "pale luminous skin with ethereal glow, slender graceful elven build, tall willowy proportions with elongated limbs, ageless ethereal beauty",
  "headPrompt": "(long pointed elf ears:1.5), prominent elongated elven ear points, distinctly pointed ear tips, pale luminous skin, elegant angular face with high cheekbones, large almond-shaped eyes, ethereal otherworldly beauty",
  "sharedAttributes": { "gender": "female", "ethnicity": "Fantasy", "skinTone": "pale", "age": "ageless" }
}
Notice: Pointed ears mentioned 3 times with weight syntax in head prompt

EXAMPLE 2 - ORC (race features heavily emphasized):
User: "male orc warrior"
{
  "bodyPrompt": "(green skin:1.4), olive-green complexion with weathered texture, massive muscular build, broad hulking frame, battle-scarred",
  "headPrompt": "(prominent lower tusks:1.5), tusks protruding from jaw, (green skin:1.4), olive-green complexion, heavy brow ridge, broad flat nose, fierce yellow eyes, strong brutish jaw",
  "sharedAttributes": { "gender": "male", "ethnicity": "Fantasy", "skinTone": "fantasy-colored", "age": "adult" }
}
Notice: Tusks and green skin heavily emphasized with weights

EXAMPLE 3 - HUMANOID (user didn't specify hair color, we pick one):
User: "Athletic elven warrior with pointed ears"
{
  "bodyPrompt": "pale ivory skin with luminous ethereal texture, athletic muscular build with lean defined muscles, tall slender elven proportions, silver-white hair pulled back",
  "headPrompt": "(long pointed elf ears:1.5), prominent elongated elven ears, distinctly pointed ear tips, pale ivory skin with ethereal texture, elegant angular face with high cheekbones, large emerald green eyes, long flowing silver-white hair",
  "sharedAttributes": { "gender": "neutral", "ethnicity": "Fantasy", "skinTone": "pale", "age": "ageless" }
}
Notice: SAME skin AND SAME hair color, but EARS are heavily emphasized with weight and repetition

EXAMPLE 2 - CREATURE:
User: "fierce wolf warrior"
{
  "bodyPrompt": "thick grey fur with darker grey markings, muscular lupine build with powerful shoulders, digitigrade legs with large paws, generic wolf face, grey furred",
  "headPrompt": "thick grey fur with darker grey markings, wolf head with elongated snout, amber eyes with vertical pupils, pointed ears, grey fur around face with lighter grey muzzle",
  "sharedAttributes": { "gender": "neutral", "ethnicity": "Fantasy", "skinTone": "fantasy-colored", "age": "adult" }
}
Notice: SAME fur color (grey) and SAME markings in BOTH prompts

IMPORTANT - RESPECT USER INTENT:
- Only fill in attributes needed for CONSISTENCY (skin, hair color, species features)
- Do NOT add personality, expression, or mood unless user specified
- Do NOT add extra features the user didn't mention or imply
- Do NOT add style keywords (anime, photorealistic, etc.) - the system handles this
- Do NOT add aspect ratios or framing terms - the system handles this
- Be detailed but concise (30-60 words per prompt)`;

// ============================================
// MAIN FUNCTIONS
// ============================================

/**
 * Analyze a character concept and return suggestions + cultural context
 */
export async function analyzeCharacterConcept(
  userDescription: string,
  expandCount: number = 0,
  previousTags: string[] = [],
  archetype?: CharacterArchetype,
  styleName?: string,
  styleDescription?: string
): Promise<CharacterConceptAnalysis> {
  try {
    // Get medium context if archetype provided
    const mediumContext = archetype ? MEDIUM_CONTEXT[archetype] : null;
    const mediumSection = mediumContext
      ? `MEDIUM/STYLE: ${mediumContext.name}
MEDIUM DESCRIPTION: ${mediumContext.description}
DRAW CULTURAL CONTEXT FROM: ${mediumContext.culturalSources}

IMPORTANT: All suggestions and cultural context MUST be specific to the ${mediumContext.name} medium.`
      : '';

    // Add specific art style context if provided
    const styleSection = styleName
      ? `
ART STYLE: ${styleName}
${styleDescription ? `STYLE DESCRIPTION: ${styleDescription}` : ''}

IMPORTANT: Tailor suggestions and cultural context to fit the "${styleName}" art style specifically. Consider visual elements, techniques, and conventions typical of this style.`
      : '';

    const userPrompt = `
CHARACTER DESCRIPTION: "${userDescription}"
${mediumSection}
${styleSection}

EXPAND COUNT: ${expandCount}
${previousTags.length > 0 ? `PREVIOUS TAGS (avoid repeating): ${previousTags.join(', ')}` : ''}

Analyze this character concept and provide:
1. 6-10 suggested visual tags appropriate for the ${styleName || mediumContext?.name || 'selected'} style (different from previous if expandCount > 0)
2. 3-5 cultural context insights about how this character type is depicted IN THIS SPECIFIC STYLE${styleName ? ` (${styleName})` : ''}
3. Inferred gender
4. Character type category

Return ONLY valid JSON.`;

    const fullPrompt = `${CONCEPT_ANALYSIS_SYSTEM_PROMPT}\n\n${userPrompt}`;

    const content = await requestCodexText(fullPrompt);

    // Parse JSON from response
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.error('Failed to parse Codex response:', content);
      return getDefaultConceptAnalysis(userDescription);
    }

    const parsed = JSON.parse(jsonMatch[0]);

    // Filter NSFW-triggering words from suggested tags
    const rawTags = parsed.suggestedTags || [];
    const filteredTags = filterNsfwTriggeringTags(rawTags);
    if (filteredTags.length < rawTags.length) {
      console.warn(`⚠️ Filtered ${rawTags.length - filteredTags.length} NSFW-triggering tags from character analysis`);
    }

    return {
      suggestedTags: filteredTags,
      culturalContext: parsed.culturalContext || [],
      inferredGender: parsed.inferredGender || 'neutral',
      characterType: parsed.characterType || 'humanoid',
      censorshipType: parsed.censorshipType || inferCensorshipType(parsed.inferredGender, parsed.characterType)
    };

  } catch (error) {
    console.error('Error analyzing character concept:', error);
    return getDefaultConceptAnalysis(userDescription);
  }
}

/**
 * Split user description into body and head prompts
 * Also classifies reference images as head, body, or both
 */
export async function splitCharacterPrompt(
  userDescription: string,
  selectedTags: string[],
  form: CharacterForm,
  archetype?: CharacterArchetype,
  styleName?: string,
  styleDescription?: string,
  referenceImages?: ReferenceImage[]
): Promise<SplitPrompts> {
  try {
    const fullDescription = selectedTags.length > 0
      ? `${userDescription}, ${selectedTags.join(', ')}`
      : userDescription;

    // Get medium context if archetype provided
    const mediumContext = archetype ? MEDIUM_CONTEXT[archetype] : null;

    // Build style-specific guidance
    const styleGuidance = styleName
      ? `
ART STYLE: ${styleName}
${styleDescription ? `STYLE DESCRIPTION: ${styleDescription}` : ''}

STYLE-SPECIFIC REQUIREMENTS:
- Generate prompts that will work well with "${styleName}" art style
- Use terminology and descriptors common in ${styleName} art
- Consider how physical features are typically rendered in this style
- Be PRECISE and SPECIFIC - generic descriptions produce poor results
- If the style is anime/manga, use anime-specific terms (e.g., "sharp angular features", "large expressive eyes")
- If photorealistic, use realistic descriptors (e.g., "subtle skin texture", "natural proportions")
- Match the level of detail and stylization appropriate for ${styleName}`
      : '';

    const userPrompt = `
CHARACTER DESCRIPTION: "${fullDescription}"

FORM: ${form}
${archetype ? `ARCHETYPE/MEDIUM: ${archetype} (${mediumContext?.name || ''})` : ''}
${styleGuidance}

CRITICAL - PRECISION IS KEY:
The image generation model needs SPECIFIC, DETAILED descriptions to produce quality results.
- BAD: "smooth skin, toned physique, dark hair" (too generic)
- GOOD: "warm ivory skin with subtle golden undertones, athletic lean build with defined shoulders and toned arms, jet black hair with natural shine"

Split this description into TWO separate prompts:
1. BODY prompt (complete figure with natural proportions; camera framing is handled separately) - Be SPECIFIC about body type, proportions, muscle definition, skin quality
2. HEAD prompt (face + hair only, 3:4 headshot) - Be SPECIFIC about facial structure, eye details, hair texture/style

Remember: The prompts should capture the CHARACTER'S essence while being optimized for ${styleName || 'the selected'} style generation.

Also extract shared attributes (gender, ethnicity, skin tone, age).
${referenceImages && referenceImages.length > 0 ? `
REFERENCE IMAGE CLASSIFICATION:
You have been provided ${referenceImages.length} reference image(s). For EACH image, classify whether it should be used for:
- "head" - if the image primarily shows face/head details (headshot, portrait, face closeup)
- "body" - if the image primarily shows body/physique (full body, torso, pose)
- "both" - if the image shows both head and body equally well (full character reference)

Include a "classifiedImages" array in your response with each image's classification in order.
` : ''}
Return ONLY valid JSON.`;

    const fullPrompt = `${PROMPT_SPLIT_SYSTEM_PROMPT}\n\n${userPrompt}`;

    const content = await requestCodexText(fullPrompt, (referenceImages || []).filter(img => img.base64).map(img => img.base64!));

    // Parse JSON from response
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.error('Failed to parse Codex response:', content);
      return getDefaultSplitPrompts(fullDescription);
    }

    const parsed = JSON.parse(jsonMatch[0]);

    // Filter NSFW-triggering words from body and head prompts
    const bodyFilterResult = filterNsfwTriggeringWords(parsed.bodyPrompt || fullDescription);
    const headFilterResult = filterNsfwTriggeringWords(parsed.headPrompt || fullDescription);

    if (bodyFilterResult.removedTerms.length > 0) {
      console.warn('⚠️ NSFW-triggering words filtered from body prompt:', bodyFilterResult.removedTerms);
    }
    if (headFilterResult.removedTerms.length > 0) {
      console.warn('⚠️ NSFW-triggering words filtered from head prompt:', headFilterResult.removedTerms);
    }

    // Build classified images array by matching parsed classifications with image IDs
    let classifiedImages: SplitPrompts['classifiedImages'] = undefined;
    if (referenceImages && referenceImages.length > 0 && parsed.classifiedImages) {
      classifiedImages = referenceImages.map((img, idx) => ({
        imageId: img.id,
        classification: parsed.classifiedImages[idx]?.classification || parsed.classifiedImages[idx] || 'both'
      }));
      console.log('📷 Reference image classifications:', classifiedImages);
    }

    return {
      bodyPrompt: bodyFilterResult.filtered,
      headPrompt: headFilterResult.filtered,
      sharedAttributes: {
        gender: parsed.sharedAttributes?.gender || 'neutral',
        ethnicity: parsed.sharedAttributes?.ethnicity || 'Mixed',
        skinTone: parsed.sharedAttributes?.skinTone || 'medium',
        age: parsed.sharedAttributes?.age
      },
      classifiedImages
    };

  } catch (error) {
    console.error('Error splitting character prompt:', error);
    return getDefaultSplitPrompts(userDescription);
  }
}

// ============================================
// FALLBACK FUNCTIONS
// ============================================

/**
 * Infer censorship type from gender and character type
 * Used as fallback when Codex doesn't return censorshipType
 */
function inferCensorshipType(
  gender: 'male' | 'female' | 'neutral' | undefined,
  characterType: string | undefined
): CensorshipType {
  const type = (characterType || '').toLowerCase();

  // Check if it's a creature type
  const creatureKeywords = ['dragon', 'slime', 'robot', 'elemental', 'ghost', 'spider',
    'insect', 'blob', 'golem', 'construct', 'spirit', 'wisp'];
  const isCreature = creatureKeywords.some(k => type.includes(k));

  // Check for hybrid creatures that might have visible sex organs
  const hybridKeywords = ['centaur', 'lamia', 'harpy', 'mermaid', 'minotaur', 'satyr',
    'werewolf', 'naga', 'sphinx', 'manticore'];
  const isHybrid = hybridKeywords.some(k => type.includes(k));

  if (isCreature && !isHybrid) {
    return 'creature_uncensored';
  }

  if (isHybrid) {
    if (gender === 'female') return 'creature_female_censored';
    if (gender === 'male') return 'creature_male_censored';
    return 'creature_uncensored'; // neutral hybrid
  }

  // Default to humanoid censorship
  if (gender === 'female') return 'female_censored';
  if (gender === 'male') return 'male_censored';
  return 'female_censored'; // Default to more coverage for neutral
}

function getDefaultConceptAnalysis(userDescription: string): CharacterConceptAnalysis {
  // Basic keyword detection for fallback
  const lowerDesc = userDescription.toLowerCase();

  const isFemale = /\b(female|woman|girl|she|her|lady|queen|princess)\b/.test(lowerDesc);
  const isMale = /\b(male|man|boy|he|his|king|prince)\b/.test(lowerDesc);
  const gender = isFemale ? 'female' : isMale ? 'male' : 'neutral';

  // Generic fallback suggestions
  const defaultTags = [
    'detailed features',
    'expressive eyes',
    'dynamic pose',
    'dramatic lighting',
    'high detail'
  ];

  const defaultContext = [
    `This character type appears frequently in fantasy and fiction media.`,
    `Consider the visual style that best fits your creative vision.`,
    `Popular depictions often emphasize distinctive features and memorable silhouettes.`
  ];

  return {
    suggestedTags: defaultTags,
    culturalContext: defaultContext,
    inferredGender: gender,
    characterType: 'humanoid',
    censorshipType: inferCensorshipType(gender, 'humanoid')
  };
}

function getDefaultSplitPrompts(description: string): SplitPrompts {
  const lowerDesc = description.toLowerCase();

  const isFemale = /\b(female|woman|girl|she|her|lady|queen|princess)\b/.test(lowerDesc);
  const isMale = /\b(male|man|boy|he|his|king|prince)\b/.test(lowerDesc);
  const gender = isFemale ? 'female' : isMale ? 'male' : 'neutral';

  return {
    bodyPrompt: description,
    headPrompt: description,
    sharedAttributes: {
      gender,
      ethnicity: 'Mixed',
      skinTone: 'medium'
    }
  };
}

// ============================================
// EXPORTS
// ============================================

export type { CharacterForm, CharacterArchetype };
